import React from "react";
import DetailedView from "@/components/layouts/DetailedView";
import { useParams } from "react-router-dom";
import { usePeople } from "@/entities/people/hooks";
import PersonTopFields from "@/components/layouts/PersonTopFields";
import PersonDetailFields from "@/components/layouts/PersonDetailFields/PersonDetailFields";
import PersonKPIs from "@/pages/people/components/PersonKPIs";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { ComputedFields } from "@/services/api/types";
import InvolvedIn from "@/components/layouts/InvolvedIn";
import { useDropdownClosing } from "@/services/hooks/useDropdownClosing";
import { usePersonActions } from "@/services/hooks/entities/person/usePersonActions";
import OptionsDropdownMenu from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { UserData } from "@/entities/users/types";
import { EntityData } from "@/services/api/types";

export interface PersonQualificationModalState {
  isOpen: boolean;
  mode: "disqualify" | "requalify" | "edit";
  initialReason: string;
}

interface PersonDetailedViewProps {
  userData: UserData;
}

const PersonDetailedView: React.FC<PersonDetailedViewProps> = ({
  userData
}) => {
  const { personId } = useParams<{ personId: string }>();

  const { isDropdownClosing } = useDropdownClosing();

  const {
    entities: [personEntity],
    isError: isPersonError
  } = usePeople([personId || ""], {
    forceRefetch: true,
    computed_fields: ComputedFields.ALL
  });

  const {
    person,
    isTasksDrawerOpen,
    toggleTasksDrawer,
    onUpdateField,
    getTopOptions,
    getCreateOptions,
    getOptions,
    renderModals,
    handleEditDisqualification,
    onCreateLead,
    onCreateDeal,
    onCreateContract
  } = usePersonActions(personEntity, userData, false, true);

  const createOptions = getCreateOptions();

  return (
    <>
      <DetailedView
        entityData={person as EntityData}
        entityType={EntityType.PERSON}
        isError={isPersonError}
        options={getOptions()}
        topOptions={getTopOptions()}
        topRightComponent={
          Object.keys(createOptions).length > 0 ? (
            <OptionsDropdownMenu
              options={createOptions}
              triggerIcon="add"
              tPrefix="detailedView.create"
              triggerDataTest="person-detailed-view-create-entity-button"
              iconSize="lg"
            />
          ) : undefined
        }
        onUpdateField={onUpdateField}
        isTasksDrawerOpen={isTasksDrawerOpen}
        toggleTasksDrawer={toggleTasksDrawer}
        topFields={
          <PersonTopFields
            data-test="person-detailed-view-top-fields"
            person={person}
            onUpdateField={onUpdateField}
            openEditQualificationModal={handleEditDisqualification}
          />
        }
        overviewContent={
          <div
            data-test="person-detailed-view-overview"
            className="w-full flex flex-col gap-6"
          >
            <PersonKPIs person={person} onUpdateField={onUpdateField} />
            <div className="w-full flex gap-6">
              <div
                data-test="person-detailed-view-detail-fields"
                className="flex-1"
              >
                <PersonDetailFields
                  person={person}
                  onUpdateField={(
                    field: string,
                    newValue: string | number | boolean | string[]
                  ) => onUpdateField(field, newValue)}
                  containerClassName="grid-cols-[minmax(0,30%),1fr]"
                />
              </div>
              <div
                data-test="person-detailed-view-involved-in"
                className="flex-1"
              >
                <InvolvedIn
                  entityId={person?.id || ""}
                  entityType={EntityType.PERSON}
                  onCreateLead={onCreateLead}
                  onCreateDeal={onCreateDeal}
                  onCreateContract={onCreateContract}
                />
              </div>
            </div>
          </div>
        }
      />
      {renderModals(isDropdownClosing, false, undefined, userData)}
    </>
  );
};

export default PersonDetailedView;
